import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {Check, X} from "lucide-react";
import {quizAnswerVerdict} from "business/quiz";
import {color, font, radius, shadow, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import {Avatar} from "components/redesign/Avatar";
import AnswerValue from "components/QuizAnswers/AnswerValue";
import {get, isEmpty, map} from "lodash";
import React from "react";
import {useTranslation} from "react-i18next";

// One question, then every member of the group on their own row: the reply on
// the left, who gave it on the right. Rows for people who have not answered
// yet are kept rather than hidden, so the card shows who the group is still
// waiting on.
const useStyles = makeStyles({
  card: {
    background: color.SURFACE,
    border: `1px solid ${color.SURFACE_MUTED}`,
    borderRadius: radius.CARD,
    boxShadow: shadow.PANEL,
    boxSizing: 'border-box',
    padding: '24px 30px',
  },
  question: {
    ...type.sessionTitle,
    color: color.TEXT,
    margin: 0,
  },
  helpText: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    fontStyle: 'italic',
    color: color.TEXT,
    opacity: 0.5,
    margin: '10px 0 0',
  },
  rows: {
    listStyle: 'none',
    margin: '10px 0 0',
    padding: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '10px 0',
    borderTop: `1px solid ${color.DIVIDER}`,
  },
  value: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    ...type.eyebrow,
    color: color.TEXT_MUTED,
    margin: '18px 0 8px',
  },
  block: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '14px 16px',
    borderRadius: radius.CONTROL,
  },
  correct: {
    background: color.SUCCESS_TINT,
  },
  incorrect: {
    background: color.DANGER_TINT,
  },
  verdictIcon: {
    flexShrink: 0,
    display: 'flex',
    marginTop: 2,
  },
  correctIcon: {
    color: color.SUCCESS,
  },
  incorrectIcon: {
    color: color.DANGER,
  },
  option: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    color: color.TEXT,
    margin: 0,
  },
  awaiting: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    fontStyle: 'italic',
    color: color.TEXT_SUBTLE,
    margin: 0,
  },
});

const QuizAnswerCard = ({index, question, rows}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const label = st(question.title || question.label, get(question, 'localeTitle') || get(question, 'localeLabel'));
  const helpText = st(get(question, 'helpText'), get(question, 'localeHelpText'));
  const {graded, correct} = quizAnswerVerdict(question);

  return (
    <div className={classes.card}>
      <p className={classes.question}>{`${t('questionPrefixQ')}${index}. ${label}`}</p>
      {!isEmpty(helpText) && <p className={classes.helpText}>{helpText}</p>}

      {graded && (
        <>
          <p className={classes.label}>{t('correctAnswer')}</p>
          <div className={classNames(classes.block, classes.correct)}>
            <span className={classes.value}>
              {map(correct, option => (
                <p key={option.key} className={classes.option}>
                  {st(option.label, option.localeLabel)}
                </p>
              ))}
            </span>
          </div>
        </>
      )}

      <ul className={classes.rows}>
        {rows.map(row => {
          const isGraded = graded && Boolean(row.quizAnswer);
          const {isCorrect} = quizAnswerVerdict(question, row.quizAnswer);
          return (
            <li key={row.key} className={classes.row}>
              <span
                className={classNames(
                  classes.value,
                  isGraded && classes.block,
                  isGraded && (isCorrect ? classes.correct : classes.incorrect)
                )}
              >
                {isGraded && (
                  <span className={classNames(classes.verdictIcon, isCorrect ? classes.correctIcon : classes.incorrectIcon)}>
                    {isCorrect ? <Check size={18} strokeWidth={3}/> : <X size={18} strokeWidth={3}/>}
                  </span>
                )}
                {row.quizAnswer
                  ? <AnswerValue question={question} quizAnswer={row.quizAnswer}/>
                  : <p className={classes.awaiting}>{t('awaitingUserResponse')}</p>}
              </span>
              <Avatar
                name={row.name}
                label={row.label}
                image={row.image}
                isDone={Boolean(row.quizAnswer)}
                variant={row.quizAnswer ? 'answer' : 'step'}
                ring
                dimWhenPending
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default QuizAnswerCard;
